import { defineStore } from 'pinia';
import { getExpected, getPaid, isPaid } from '@/composables/useAppointmentCalculations';
import { useUserStore } from '@/stores/user';
import { collection, onSnapshot, where, query } from 'firebase/firestore';
import { firestore } from '@/firebase';

export const useAppointmentStore = defineStore('appointment', {
  state: () => ({
    appointments: {},
    unsubs: {}
  }),
  persist: { paths: ['appointments'] },
  getters: {
    getExpected: () => getExpected,
    getPaid: () => getPaid,
    isPaid: () => isPaid
  },
  actions: {
    upsert({ id, value }) {
      this.appointments = { ...this.appointments, [id]: value };
    },
    remove(id) {
      const appointments = { ...this.appointments };
      delete appointments[id];
      this.appointments = appointments;
    },
    clearUnsubs() {
      for (const [, unsub] of Object.entries(this.unsubs)) {
        if (typeof unsub === 'function') unsub();
      }
      this.unsubs = {};
    },
    clear() {
      this.clearUnsubs();
      this.appointments = {};
    },
    /**
     * Registers a real-time listener for appointments in a single past month.
     * Used when the user navigates to a month outside the primary window (2 months ago to future).
     * On "removed" we only remove from store if the doc's date is still in range (actual delete);
     * otherwise the doc may have moved to another range (date changed).
     */
    registerMonthSnapshot(d) {
      const year = d.getFullYear();
      const month = d.getMonth();
      const monthStart = new Date(year, month, 1).getTime();
      const monthEnd = new Date(year, month + 1, 1).getTime();
      const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

      if (this.unsubs[monthKey]) return;

      const userStore = useUserStore();
      let monthQuery = query(collection(firestore, 'appointments'), where('date', '>=', monthStart), where('date', '<', monthEnd));
      if (!userStore.hasPermission('appointments:manage')) {
        monthQuery = query(monthQuery, where('lash', '==', userStore.userId));
      }

      const unsub = onSnapshot(monthQuery, (snapshot) => {
        snapshot.docChanges().forEach((element) => {
          const id = element.doc.id;
          const elementData = { ...element.doc.data(), id };

          if (element.type === 'added' || element.type === 'modified') {
            this.upsert({ id, value: elementData });
            return;
          }

          if (element.type === 'removed') {
            const current = this.appointments[id];
            const stillInRange =
              current &&
              typeof current.date === 'number' &&
              current.date >= monthStart &&
              current.date < monthEnd;
            if (stillInRange) {
              this.remove(id);
            }
          }
        });
      },
      (error) => {
        console.error('Appointment - Error registering snapshot', error);
      });

      this.unsubs[monthKey] = unsub;
    }
  }
});
